export const isFilledItem = (item) => !item?.__placeholder;

/** 投資型保單 → 保單借款預設負債比例（利息不自動帶入） */
export const POLICY_LOAN_RATIO = 0.5;

export const isPolicyLoanOption = (option = {}) => {
    const id = Number(option?.value ?? option?.id ?? option?.option_id);
    if (id === 28) return true;
    const name = String(option?.label ?? option?.name ?? option?.option_name ?? '');
    return name.includes('保單借款');
};

export const suggestedPolicyLoanAmount = (assetAmount) => {
    const n = Number(assetAmount);
    if (!Number.isFinite(n) || n <= 0) return '';
    return String(Math.round(n * POLICY_LOAN_RATIO));
};

/**
 * 月攤還（僅畫面）：月支出 ÷ 負債餘額。
 * 回傳比率（0～1+）；無有效數字時回 null。
 */
export const calcMonthlyAmortization = (amount, monthlyExpense) => {
    const balance = Number(amount);
    const monthly = Number(monthlyExpense);
    if (!Number.isFinite(balance) || balance <= 0) return null;
    if (!Number.isFinite(monthly) || monthly < 0) return null;
    if (monthly === 0) return 0;
    return monthly / balance;
};

/** 月攤還顯示：比率轉成百分比字串（不入庫） */
export const formatMonthlyAmortization = (ratio) => {
    if (ratio === null || ratio === undefined || !Number.isFinite(Number(ratio))) return '—';
    const pct = Number(ratio) * 100;
    const abs = Math.abs(pct);
    const body =
        abs >= 100
            ? abs.toLocaleString('zh-TW', { maximumFractionDigits: 1 })
            : abs.toLocaleString('zh-TW', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2
              });
    if (pct > 0) return `${body}%`;
    if (pct < 0) return `-${body}%`;
    return '0%';
};

export const normalizeFlatAmountInput = (value) =>
    String(value ?? '').replace(/[^\d]/g, '');

export const formatFlatAmountInput = (value) => {
    const digits = normalizeFlatAmountInput(value);
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/** 只將真正有輸入的資料列視為可儲存；純預設空白列會被排除。 */
export const isTouchedDraft = (draft = {}) =>
    Boolean(String(draft.name || '').trim()) ||
    !isBlankSheetValue(draft.amount) ||
    !isBlankSheetValue(draft.monthly_interest_expense);

export const shouldShowEmptyCategoryHint = (readonly, rowCount) =>
    !readonly && Number(rowCount) === 0;

/** 完成版本前先保存畫面資料；驗證或保存失敗時不得繼續鎖定版本。 */
export const saveFlatSheetBeforeFinish = async ({ enabled, prepare, persist }) => {
    if (!enabled) return true;
    const prepared = prepare?.();
    if (!prepared?.ok) return false;
    return (await persist(prepared.payload, { silent: true })) !== false;
};

/** API 的業務錯誤可能仍是 HTTP 200，必須檢查 body.status。 */
export const assertSuccessfulSheetResponse = (response, fallbackMessage) => {
    if (response?.status !== 1) {
        throw new Error(response?.message || fallbackMessage);
    }
    return response;
};

export const isRequiredFlag = (value) =>
    value === 1 || value === true || String(value).toLowerCase() === '1' || String(value).toLowerCase() === 'true';

export const isBlankSheetValue = (value) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** 草稿與完成共用的單列檢核；明確允許數值 0。
 *  市值／負債餘額、月收支是否必填，依選項 value_required／cash_flow_required。
 */
export const validateFlatDraft = (draft, option = {}, fields = [], labels = {}) => {
    const errors = {};
    if (!String(draft?.name || '').trim()) errors.name = '請填寫名稱';

    const amountRequired = isRequiredFlag(option?.value_required);
    const monthlyRequired = isRequiredFlag(option?.cash_flow_required);

    if (amountRequired && isBlankSheetValue(draft?.amount)) {
        errors.amount = '請填寫市值／負債餘額';
    }
    if (monthlyRequired && isBlankSheetValue(draft?.monthly_interest_expense)) {
        errors.monthly = `請填寫${labels.monthly || '每月金額'}`;
    }

    fields.forEach((field) => {
        if (isBlankSheetValue(draft?.fieldValues?.[field.id])) errors[`f-${field.id}`] = `請填寫${field.name}`;
    });
    return errors;
};

export const groupRowsByType = (rows = []) => {
    return rows.reduce((groups, row) => {
        const key = Number(row.type_id);
        if (!groups[key]) groups[key] = [];
        groups[key].push(row);
        return groups;
    }, {});
};

export const buildDisplayRows = (typeId, rows = [], options = [], seed = false) => {
    const filled = rows.filter((row) => Number(row.type_id) === Number(typeId));
    if (!seed) return filled;
    const filledOptionIds = new Set(filled.map((row) => Number(row.option_id)));
    const placeholders = options
        .filter((option) => !filledOptionIds.has(Number(option.value ?? option.id)))
        .map((option) => ({
            type_id: Number(typeId),
            option_id: Number(option.value ?? option.id),
            option_name: option.label ?? option.name ?? '',
            name: '',
            // 空白字串，勿用 0：數字 0 會被當成已填，強制補齊補充資訊並擋住儲存
            amount: '',
            monthly_interest_expense: '',
            fields: [],
            __placeholder: true
        }));
    return [...filled, ...placeholders];
};

export const buildActionableSuggestions = (
    targetClass,
    targetTypeId,
    rows = [],
    relations = []
) => {
    const realRows = rows.filter(isFilledItem);
    return relations.flatMap((relation) => {
        const missingOptionId = Number(
            targetClass === 0
                ? relation.asset_option_id
                : relation.liability_option_id
        );
        const filledOptionId = Number(
            targetClass === 0
                ? relation.liability_option_id
                : relation.asset_option_id
        );
        const missingTypeId = Number(
            targetClass === 0
                ? relation.asset_type_id ?? relation.asset_option?.type_id
                : relation.liability_type_id ?? relation.liability_option?.type_id
        );
        if (missingTypeId && missingTypeId !== Number(targetTypeId)) return [];
        const existingMissing = realRows.filter(
            (row) => Number(row.option_id) === missingOptionId
        );
        const sources = realRows.filter((row) => Number(row.option_id) === filledOptionId);
        const missingOptionName =
            targetClass === 0
                ? relation.asset_option_name ?? relation.asset_option?.name ?? ''
                : relation.liability_option_name ?? relation.liability_option?.name ?? '';
        return sources
            .filter((source) => {
                const sourceName = String(source.name || '').trim();
                // 已有同名對應列則不再建議；無名則以「是否已有任一筆」判斷
                if (sourceName) {
                    return !existingMissing.some(
                        (row) => String(row.name || '').trim() === sourceName
                    );
                }
                return existingMissing.length === 0;
            })
            .map((source) => ({
                filled_name: source.name,
                filled_option_id: filledOptionId,
                filled_amount: Number(source.amount) || 0,
                missing_option_id: missingOptionId,
                missing_type_id: Number(targetTypeId),
                missing_type: targetClass === 0 ? 'asset' : 'liability',
                missing_option_name: missingOptionName,
                suggested_amount: isPolicyLoanOption({
                    id: missingOptionId,
                    name: missingOptionName
                })
                    ? suggestedPolicyLoanAmount(source.amount)
                    : ''
            }));
    });
};

export const calculateFlatTotals = (rows = [], classMap = {}) =>
    rows.filter(isFilledItem).reduce(
        (totals, row) => {
            const amount = Number(row.amount) || 0;
            const monthly = Number(row.monthly_interest_expense) || 0;
            if (Number(classMap[row.type_id]) === 1) {
                totals.liabilities += amount;
                totals.expenses += monthly;
            } else {
                totals.assets += amount;
                totals.income += monthly;
            }
            totals.net = totals.assets - totals.liabilities;
            totals.cashFlow = totals.income - totals.expenses;
            return totals;
        },
        { assets: 0, liabilities: 0, net: 0, income: 0, expenses: 0, cashFlow: 0 }
    );

export const calculateSelectedSum = (cells = [], classMap = {}) => cells.reduce((sum, cell) => {
    const value = Number(cell?.value) || 0;
    return sum + (Number(classMap[cell?.type_id]) === 1 ? -value : value);
}, 0);

/** 合計工具的點擊接收者；唯讀時 disabled input 不會穩定冒泡，改由欄位接收。 */
export const sumCellInteractionMode = ({ sumToolOpen = false, readonly = false } = {}) =>
    sumToolOpen && readonly ? 'container' : 'input';

const serializeRow = (row) => ({
    option_id: Number(row.option_id),
    name: String(row.name || '').trim(),
    amount: Number(row.amount) || 0,
    monthly_interest_expense: Number(row.monthly_interest_expense) || 0,
    fields: Array.isArray(row.fields) ? row.fields : []
});

/** 後台 API 規格是每次送出該 type 的完整清單；placeholder 永遠不可進 payload。 */
export const buildTypeSavePayload = (
    rows,
    typeId,
    { mode = 'add', recordId = null, item }
) => {
    const current = rows
        .filter(isFilledItem)
        .filter((row) => Number(row.type_id) === Number(typeId));
    if (mode === 'edit') {
        return current.map((row) =>
            String(row.data_id) === String(recordId)
                ? serializeRow(item)
                : serializeRow(row)
        );
    }
    return [...current.map(serializeRow), serializeRow(item)];
};
